/**
 * The Open tickets report (the open-tickets roadmap, P6; ADRs 0183, 0185):
 * closed is the goal, not logged. Each tech's tickets from every day, as
 * their tray lists them: assigned to them in Autotask, or with their time,
 * logged here or in Autotask. It's as of now, so the filters' days don't
 * apply; each ticket's age counts from its first logging (or, for one only
 * assigned, its making in Autotask) to the tech's own today.
 *
 * By default every ticket not closed; the State choice is the report's own,
 * kept in the URL (`state`) as the filters change. A ticket is named by its
 * number, its link to Autotask carrying the ID; a HIPAA client's title is
 * withheld by the API (ADR 0086). "Download CSV" saves the same rows.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { useSearchParams } from "react-router";

import type { OpenTicketRow, TechOpenTickets, TicketReason, TicketState } from "../../api/types";
import { useToday } from "../../app/useSelectedDay";
import { useSession } from "../../app/session-context";
import { HipaaBadge } from "../../components/ui/Badge";
import { Panel } from "../../components/ui/Panel";
import { formatShortDay, todayIn } from "../../lib/dates";
import { saveBlob } from "../../lib/download";
import { formatDuration } from "../../lib/durations";
import { possessive } from "../../lib/possessive";
import { count, errorMessage, pillButton, retryOnce, TICKET_STATE_LABELS } from "./common";
import { useReportFilters } from "./filters";
import { TicketLink, TicketStateBadge } from "./ReportCells";

/**
 * Where each state comes in the State list: a Record, so a new state can't
 * be left out, as P9's reopening was (#861).
 */
const STATE_ORDER: Record<TicketState, number> = {
  open: 0,
  reopening: 1,
  close_failed: 2,
  closing: 3,
  unknown: 4,
  closed: 5,
};

/** The State choice: none (every one but closed), or one state. */
const STATE_CHOICES: { value: TicketState | ""; label: string }[] = [
  { value: "", label: "Not closed" },
  ...(Object.keys(STATE_ORDER) as TicketState[])
    .sort((a, b) => STATE_ORDER[a] - STATE_ORDER[b])
    .map((state) => ({ value: state, label: TICKET_STATE_LABELS[state] })),
];

const REASONS: Record<TicketReason, string> = {
  assigned: "Assigned",
  logged_axon_time: "Logged in Axon Time",
  logged_autotask: "Logged in Autotask",
};

const NONE = "No open tickets.";
const NONE_IN_STATE = "No tickets in that state.";
const headCell = "pb-2 pr-3";
const inputClass =
  "h-9 rounded-input border border-line bg-card px-3 text-sm text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

function techName(tech: TechOpenTickets): string {
  return tech.user.display_name ?? tech.user.email ?? "Unnamed user";
}

function stateIn(value: string | null): TicketState | "" {
  return STATE_CHOICES.find((choice) => choice.value === value)?.value ?? "";
}

/** An instant's day in the tech's own zone, or a dash. */
function dayOf(iso: string | null, zone: string): string {
  return iso === null ? "—" : formatShortDay(todayIn(zone, new Date(iso)));
}

function Row({ row, zone }: { row: OpenTicketRow; zone: string }) {
  return (
    <tr className="border-t border-line align-top">
      <td className="py-3 pr-3">
        <span className="flex flex-col gap-0.5">
          <span className="font-mono text-xs whitespace-nowrap">
            <TicketLink number={row.ticket_number} url={row.ticket_url} />
          </span>
          {row.title !== null ? <span className="text-xs break-words text-muted">{row.title}</span> : null}
        </span>
      </td>
      <td className="py-3 pr-3">
        <span className="flex flex-col gap-1">
          {row.client ? <span className="font-medium">{row.client.name}</span> : <span className="text-muted">No client</span>}
          {row.client?.is_hipaa ? <HipaaBadge /> : null}
        </span>
      </td>
      <td className="py-3 pr-3">{row.status_label ?? (row.status === null ? "—" : String(row.status))}</td>
      <td className="py-3 pr-3">
        <TicketStateBadge state={row.state} />
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">
        {row.age_days === null ? (
          "—"
        ) : (
          <span className="flex flex-col">
            <span className="font-semibold tracking-[-0.02em]">{`${String(row.age_days)} d`}</span>
            <span className="text-xs text-muted">
              {row.age_counts_from === "created" ? "from created" : "from first logged"}
            </span>
          </span>
        )}
      </td>
      <td className="py-3 pr-3">{row.reasons.map((reason) => REASONS[reason]).join(", ")}</td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <span className="font-semibold tracking-[-0.02em]">{formatDuration(row.own_logged_seconds)}</span>
        {row.logged_seconds !== row.own_logged_seconds ? (
          <span className="block text-xs text-muted">{`of ${formatDuration(row.logged_seconds)}`}</span>
        ) : null}
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">{dayOf(row.first_logged_at, zone)}</td>
      <td className="py-3 whitespace-nowrap">{dayOf(row.last_logged_at, zone)}</td>
    </tr>
  );
}

function TechSection({ tech, none }: { tech: TechOpenTickets; none: string }) {
  const headingId = useId();
  const name = techName(tech);
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 id={headingId} className="text-base font-[650] tracking-[-0.015em]">
          {name}
        </h3>
        <p className="text-[13px] text-muted">
          {[
            count(tech.tickets, "ticket", "tickets"),
            ...(tech.oldest_age_days === null ? [] : [`oldest ${String(tech.oldest_age_days)} d`]),
            `Times in ${tech.user.timezone}`,
          ].join(" · ")}
        </p>
      </div>
      {tech.rows.length === 0 ? (
        <p className="rounded-row bg-panel-2 px-4 py-3 text-muted">{none}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <caption className="sr-only">{`${possessive(name)} tickets, oldest first`}</caption>
            <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
              <tr>
                <th scope="col" className={headCell}>
                  Ticket
                </th>
                <th scope="col" className={headCell}>
                  Client
                </th>
                <th scope="col" className={headCell}>
                  Autotask status
                </th>
                <th scope="col" className={headCell}>
                  State
                </th>
                <th scope="col" className={headCell}>
                  Age
                </th>
                <th scope="col" className={headCell}>
                  Why it&apos;s theirs
                </th>
                <th scope="col" className={headCell}>
                  Time logged
                </th>
                <th scope="col" className={headCell}>
                  First logged
                </th>
                <th scope="col" className="pb-2">
                  Last logged
                </th>
              </tr>
            </thead>
            <tbody>
              {tech.rows.map((row) => (
                <Row key={row.autotask_ticket_id} row={row} zone={tech.user.timezone} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function OpenTicketsReport() {
  const { api, me } = useSession();
  const headingId = useId();
  const stateId = useId();
  const [filters] = useReportFilters();
  const [params, setParams] = useSearchParams();
  const today = useToday(me.timezone);
  const chosen = stateIn(params.get("state"));
  const states: TicketState[] = chosen === "" ? [] : [chosen];
  // As of now: the days aren't part of what it asks.
  const asked = { userIds: filters.userIds, clientIds: filters.clientIds, states };
  const report = useQuery({
    queryKey: ["reports", "open-tickets", asked],
    queryFn: () => api.getOpenTicketsReport(filters, states),
    retry: retryOnce,
  });
  const download = useMutation({
    mutationFn: () => api.downloadOpenTicketsCsv(filters, states),
    onSuccess: (blob) => {
      saveBlob(blob, `open-tickets-${today}.csv`);
    },
  });

  return (
    <Panel aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-lg font-semibold tracking-tight">
            Open tickets
          </h2>
          <p className="text-[13.5px] text-muted">
            Each tech&apos;s tickets that aren&apos;t closed yet, from every day: assigned to them in Autotask, or with
            their time, logged here or in Autotask. Closed is the goal, not logged.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor={stateId} className="text-xs font-semibold text-muted">
            State
          </label>
          <select
            id={stateId}
            className={inputClass}
            value={chosen}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              next.delete("state");
              if (event.target.value !== "") {
                next.set("state", event.target.value);
              }
              setParams(next, { replace: true });
            }}
          >
            {STATE_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={pillButton}
            disabled={!report.isSuccess || download.isPending}
            onClick={() => {
              download.mutate();
            }}
          >
            Download CSV
          </button>
        </div>
      </div>
      {download.isError ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not downloaded: {errorMessage(download.error)}
        </p>
      ) : null}
      {report.isPending ? (
        <p role="status" className="text-muted">
          Loading the report…
        </p>
      ) : report.isError ? (
        <p role="alert" className="text-status-needs-client-fg">
          {errorMessage(report.error)}
        </p>
      ) : report.data.techs.length === 0 ? (
        <p className="py-6 text-center text-muted">{chosen === "" ? NONE : NONE_IN_STATE}</p>
      ) : (
        report.data.techs.map((tech) => <TechSection key={tech.user.id} tech={tech} none={chosen === "" ? NONE : NONE_IN_STATE} />)
      )}
    </Panel>
  );
}
